import React, { useState, useEffect, useRef } from 'react';
import { ToastContainer } from 'react-toastify';
import { toast } from 'react-toastify';
import MobileOrderList from './order_table_components/MobileOrderList';
import DesktopOrderTable from './order_table_components/DesktopOrderTable';
import StatusUpdateModal from './order_table_components/StatusUpdateModal';
import TrackingModal from './order_table_components/TrackingModal';
import OrderTableStyles from './order_table_components/OrderTableStyles';
import { updateOrderCallStatus } from '../orders-controller/order-controller';

const OrderTable = ({
  orders,
  toggleDropdown,
  openDropdown,
  handleUpdateStatus,
  handleDeleteOrder,
  translateStatus,
  getStatusColor,
  formatDate,
  getClientFullName,
  setOpenDropdown,
  selectedOrders = [],
  handleOrderSelection = () => { },
  refreshData = () => { },           // Add this prop for data refresh
  refreshDashboard = () => { }       // Add this prop for dashboard refresh
}) => {
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, isFixed: false });
  const activeDropdownId = useRef(null);
  const [newlyAddedOrders, setNewlyAddedOrders] = useState({});
  const prevOrdersRef = useRef(orders.length);
  const [isFooterExpanded, setIsFooterExpanded] = useState(false);
  // State variables for confirmation modals
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [statusToUpdate, setStatusToUpdate] = useState(null);
  const [orderToUpdate, setOrderToUpdate] = useState(null);
  const [statusKey, setStatusKey] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // State variables for tracking modal
  const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false);
  const [orderToTrack, setOrderToTrack] = useState(null);
  const [isTrackingSubmitting, setIsTrackingSubmitting] = useState(false);

  // Handle call status change
  const handleCallStatusChange = async (orderId, currentCallStatus) => {
    try {
      // Determine new call type based on current status
      let newCallType;
      if (currentCallStatus === null) {
        // If call is null (gray), make it green - send 1
        newCallType = 1;
      } else if (currentCallStatus === true || currentCallStatus === 1) {
        // If call is green (true/1), make it red - send 0
        newCallType = 0;
      } else if (currentCallStatus === false || currentCallStatus === 0) {
        // If call is red (false/0), make it green - send 1
        newCallType = 1;
      }

      await updateOrderCallStatus(orderId, newCallType);
      
      // Refresh the data to show updated status
      refreshData();
      refreshDashboard();
      
      // Show success message
      toast.success("Statut d'appel mis à jour avec succès", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error('Error updating call status:', error);
      
      // Show error message
      toast.error("Erreur lors de la mise à jour du statut d'appel", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  // Function to handle opening the tracking modal
  const handleTrackOrder = (order) => {
    setOrderToTrack(order);
    setIsTrackingModalOpen(true);
  };

  // Function to handle tracking confirmation
  const handleConfirmTracking = async () => {
    if (!orderToTrack) return;

    setIsTrackingSubmitting(true);

    try {
      // Here you would make an API call to track the order
      // For now, we'll simulate a successful tracking request
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Close the modal first
      setIsTrackingModalOpen(false);

      // Then refresh data (similar to status update)
      refreshData();      // Refresh the orders table
      refreshDashboard(); // Refresh the status summary

      // Reset state
      setOrderToTrack(null);

      // Show success toast
      toast.success("La demande de suivi a été envoyée avec succès", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error('Error tracking order:', error);

      // Close modal and reset state
      setIsTrackingModalOpen(false);
      setOrderToTrack(null);

      // Show error toast
      toast.error("Une erreur s'est produite lors de la demande de suivi", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsTrackingSubmitting(false);
    }
  };

  // Modify the handleStatusChange function to accept a skipConfirmation parameter
  const handleStatusChange = (orderId, newStatus, newStatusKey, skipConfirmation = false) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;

    if (skipConfirmation) {
      // Skip confirmation and directly update the status
      handleUpdateStatus(orderId, newStatus, newStatusKey);
    } else {
      // Show confirmation modal as before
      setOrderToUpdate(order);
      setStatusToUpdate(newStatus);
      setStatusKey(newStatusKey);
      setIsStatusModalOpen(true);
    }

    if (openDropdown !== null) {
      setOpenDropdown(null);
    }
  };

  // Confirmed status update handler
  const handleConfirmStatusUpdate = async () => {
    if (!orderToUpdate || !statusToUpdate) return;

    setIsSubmitting(true);

    try {
      const response = await handleUpdateStatus(orderToUpdate.id, statusToUpdate, statusKey);

      // Close the modal first
      setIsStatusModalOpen(false);
      setOrderToUpdate(null);
      setStatusToUpdate(null);
      setStatusKey(null);

      // Then handle response and show toast
      if (response && response.status === 500) {
        // Use setTimeout to ensure the toast displays after state updates
        setTimeout(() => {
          toast.error(response.response.data.errorMessage || "Une erreur s'est produite", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }, 0);
      } else {
        // Refresh data and dashboard after successful status update
        refreshData();      // Refresh the orders table
        refreshDashboard(); // Refresh the status summary
        
        // Success toast
        setTimeout(() => {
          toast.success("Le statut a été mis à jour avec succès", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }, 0);
      }
    } catch (error) {
      console.error('Error updating status:', error);

      // Close modal before showing toast
      setIsStatusModalOpen(false);
      setOrderToUpdate(null);
      setStatusToUpdate(null);
      setStatusKey(null);

      // Use setTimeout to ensure the toast displays after state updates
      setTimeout(() => {
        toast.error("Une erreur s'est produite lors de la mise à jour du statut", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }, 0);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isMobileView = () => {
    return window.innerWidth < 1024;
  };

  const positionDropdown = (buttonId) => {
    const button = document.getElementById(`status-button-${buttonId}`);
    if (!button) return;

    const rect = button.getBoundingClientRect();

    if (isMobileView()) {
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        isFixed: true
      });
    } else {
      setDropdownPosition({
        top: 0,
        left: 0,
        isFixed: false
      });
    }
  };

  useEffect(() => {
    if (openDropdown !== null && isMobileView()) {
      activeDropdownId.current = openDropdown;
      positionDropdown(openDropdown);

      const handleScrollOrResize = () => {
        if (activeDropdownId.current !== null) {
          requestAnimationFrame(() => {
            positionDropdown(activeDropdownId.current);
          });
        }
      };

      window.addEventListener('scroll', handleScrollOrResize, { passive: true });
      window.addEventListener('resize', handleScrollOrResize, { passive: true });

      const possibleContainers = ['main', '.overflow-auto', '.overflow-y-auto'];
      possibleContainers.forEach(selector => {
        const containers = document.querySelectorAll(selector);
        containers.forEach(container => {
          container.addEventListener('scroll', handleScrollOrResize, { passive: true });
        });
      });

      return () => {
        window.removeEventListener('scroll', handleScrollOrResize);
        window.removeEventListener('resize', handleScrollOrResize);

        possibleContainers.forEach(selector => {
          const containers = document.querySelectorAll(selector);
          containers.forEach(container => {
            container.removeEventListener('scroll', handleScrollOrResize);
          });
        });
      };
    }
  }, [openDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown !== null) {
        const button = document.getElementById(`status-button-${openDropdown}`);
        const menu = document.getElementById(`status-menu-${openDropdown}`);

        if (
          (button && !button.contains(event.target)) &&
          (menu && !menu.contains(event.target))
        ) {
          setOpenDropdown(null);
          activeDropdownId.current = null;
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown, setOpenDropdown]);

  const handleToggleDropdown = (id) => {
    if (openDropdown === id) {
      setOpenDropdown(null);
      activeDropdownId.current = null;
    } else {
      toggleDropdown(id);
      positionDropdown(id);
      activeDropdownId.current = id;
    }
  };

  useEffect(() => {
    const getSeenOrderIds = () => {
      try {
        const storedIds = localStorage.getItem('seenOrderIds');
        return storedIds ? JSON.parse(storedIds) : [];
      } catch (e) {
        console.error('Error reading from localStorage:', e);
        return [];
      }
    };

    const saveSeenOrderIds = (ids) => {
      try {
        localStorage.setItem('seenOrderIds', JSON.stringify(ids));
      } catch (e) {
        console.error('Error saving to localStorage:', e);
      }
    };

    if (orders.length > 0) {
      const seenOrderIds = getSeenOrderIds();
      const currentOrderIds = orders.map(order => order.id);
      const newOrderIds = currentOrderIds.filter(id => !seenOrderIds.includes(id));

      if (newOrderIds.length > 0) {
        const newHighlights = {};
        newOrderIds.forEach(id => {
          newHighlights[id] = true;
        });

        setNewlyAddedOrders(newHighlights);

        setTimeout(() => {
          setNewlyAddedOrders({});
        }, 7000);

        saveSeenOrderIds([...seenOrderIds, ...newOrderIds]);
      }

      prevOrdersRef.current = orders.length;
    }
  }, [orders]);

  // Include shared status options and transition logic
  const allStatusOptions = [
    { value: 'En attente', label: 'En attente', color: 'bg-amber-100 text-amber-800', key: 'pending' },
    { value: 'Prêt à expédier', label: 'Prêt à expédier', color: 'bg-emerald-100 text-emerald-800', key: 'readyToShip' },
    { value: 'Expédié', label: 'Expédié', color: 'bg-blue-100 text-blue-800', key: 'shipped' },
    { value: 'Confirmé', label: 'Confirmé', color: 'bg-green-100 text-green-800', key: 'confirmed' },
    { value: 'Livré', label: 'Livré', color: 'bg-green-100 text-green-800', key: 'delivered' },
    { value: 'Annulé', label: 'Annulé', color: 'bg-red-100 text-red-800', key: 'canceled' },
    { value: 'Abandonné', label: 'Abandonné', color: 'bg-gray-100 text-gray-800', key: 'abandoned' },
    { value: 'Payé', label: 'Payé', color: 'bg-indigo-100 text-indigo-800', key: 'paid' },
    { value: 'Retour', label: 'Retour', color: 'bg-purple-100 text-purple-800', key: 'return' },
    { value: 'Retour reçu', label: 'Retour reçu', color: 'bg-purple-100 text-purple-800', key: 'returnReceived' },
    { value: 'Déchet', label: 'Déchet', color: 'bg-red-100 text-red-900', key: 'dechet' }
  ];

  const getAllowedStatusOptions = (currentStatus) => {
    const currentStatusKey = getStatusKey(currentStatus);

    const statusTransitions = {
      'pending': ['confirmed', 'canceled'],
      'confirmed': ['canceled', 'readyToShip', 'shipped', 'paid', 'delivered'],
      'readyToShip': ['shipped', 'delivered', 'canceled', 'paid', 'return', 'returnReceived'],
      'shipped': ['delivered', 'canceled', 'paid', 'return', 'returnReceived'],
      'delivered': ['return', 'paid', 'returnReceived'],
      'canceled': [],
      'abandoned': ['paid'],
      'paid': [],
      'return': ['returnReceived'],
      'returnReceived': ['dechet'],
      'dechet': []
    };

    const allowedStatusKeys = statusTransitions[currentStatusKey] || [];
    const allowed = allStatusOptions.filter(option => allowedStatusKeys.includes(option.key));

    return allowed;
  };

  const getStatusKey = (statusValue) => {
    if (!statusValue) return '';

    const statusValueToKeyMap = {
      'En attente': 'pending',
      'Prêt à expédier': 'readyToShip',
      'Expédié': 'shipped',
      'Confirmé': 'confirmed',
      'Livré': 'delivered',
      'Annulé': 'canceled',
      'Abandonné': 'abandoned',
      'Payé': 'paid',
      'Retour': 'return',
      'Retour reçu': 'returnReceived',
      'Déchet': 'dechet'
    };

    if (statusValueToKeyMap[statusValue]) {
      return statusValueToKeyMap[statusValue];
    }

    const option = allStatusOptions.find(opt => opt.value === statusValue);
    if (option) {
      return option.key;
    }

    const isAlreadyKey = Object.values(statusValueToKeyMap).includes(statusValue);
    if (isAlreadyKey) {
      return statusValue;
    }

    const normalizedKey = statusValue.toLowerCase().replace(/\s+/g, '_').replace(/[àáâãäå]/g, 'a').replace(/[èéêë]/g, 'e');
    return normalizedKey;
  };

  const getStatusLabel = (statusValue) => {
    const option = allStatusOptions.find(opt => opt.value === statusValue);
    if (option) {
      return option.label;
    }

    const optionByKey = allStatusOptions.find(opt => opt.key === statusValue);
    if (optionByKey) {
      return optionByKey.label;
    }

    return statusValue;
  };

  const getStatusInfo = (statusValue) => {
    const option = allStatusOptions.find(opt => opt.value === statusValue);
    if (option) {
      return {
        label: option.label,
        color: option.color
      };
    }

    const optionByKey = allStatusOptions.find(opt => opt.key === statusValue);
    if (optionByKey) {
      return {
        label: optionByKey.label,
        color: optionByKey.color
      };
    }

    return {
      label: statusValue,
      color: 'bg-gray-100 text-gray-800'
    };
  };


  // Add a function to toggle footer expansion
  const toggleFooterExpand = () => {
    setIsFooterExpanded(!isFooterExpanded);
  };

  // Add or update the formatPrice function in the OrderTable component
  const formatPrice = (price) => {
    return `${parseFloat(price || 0).toFixed(2)} TND`;
  };

  return (
    <div>
      <OrderTableStyles />
      
      <MobileOrderList 
        orders={orders}
        getAllowedStatusOptions={getAllowedStatusOptions}
        getStatusInfo={getStatusInfo}
        handleOrderSelection={handleOrderSelection}
        openDropdown={openDropdown}
        handleToggleDropdown={handleToggleDropdown}
        dropdownPosition={dropdownPosition}
        handleStatusChange={handleStatusChange}
        formatDate={formatDate}
        getClientFullName={getClientFullName}
        handleDeleteOrder={handleDeleteOrder}
        selectedOrders={selectedOrders}
        newlyAddedOrders={newlyAddedOrders}
        handleTrackOrder={handleTrackOrder} // Add this prop
        isFooterExpanded={isFooterExpanded}
        toggleFooterExpand={toggleFooterExpand}
        formatPrice={formatPrice}
        handleCallStatusChange={handleCallStatusChange} // Add this prop
      />
      
      <DesktopOrderTable
        orders={orders}
        getAllowedStatusOptions={getAllowedStatusOptions}
        getStatusInfo={getStatusInfo}
        formatDate={formatDate}
        getClientFullName={getClientFullName}
        handleStatusChange={handleStatusChange}
        getStatusLabel={getStatusLabel}
        handleDeleteOrder={handleDeleteOrder}
        selectedOrders={selectedOrders}
        handleOrderSelection={handleOrderSelection}
        newlyAddedOrders={newlyAddedOrders}
        handleTrackOrder={handleTrackOrder} // Add this prop
        isFooterExpanded={isFooterExpanded}
        toggleFooterExpand={toggleFooterExpand}
        formatPrice={formatPrice}
        refreshData={refreshData} // Add this prop for refreshing data
        handleCallStatusChange={handleCallStatusChange} // Pass single handler to avoid duplicate calls
      />
      
      <StatusUpdateModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        onSubmit={handleConfirmStatusUpdate}
        isSubmitting={isSubmitting}
        orderToUpdate={orderToUpdate}
        statusToUpdate={statusToUpdate}
        getStatusInfo={getStatusInfo}
      />
      
      {/* Add the tracking modal */}
      <TrackingModal
        isOpen={isTrackingModalOpen}
        onClose={() => setIsTrackingModalOpen(false)}
        onSubmit={handleConfirmTracking}
        isSubmitting={isTrackingSubmitting}
        order={orderToTrack}
      />

    </div>
  );
};

export default OrderTable;